import type { ReactNode } from 'react';
import {
  CheckCircleIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline';

// ------------------------------------------------------------------
// Callout — a NAMED, actionable notice (story 4-2e; P10 §2.3's error/
// empty/loading family, the member it was missing).
// ------------------------------------------------------------------
//
// The gap this fills. The DS already had the two ends of the range:
// `EmptyState` (a full-width centred card that OWNS the content area — it
// is what a list renders INSTEAD of rows) and `FormError` (one red
// sentence under a form). Between them sat every "this specific thing went
// wrong, here is the specific way forward" surface, and because there was
// no primitive for it, each screen grew its own `<p className="text-xs
// text-amber-600 …">` — six of them in `TemplatePublishBar` alone, each
// picking its own colour, its own live-region behaviour (usually none) and
// its own idea of whether to offer an action at all.
//
// P04 §7 ¶4 is what forced the primitive: ten failure states, each of which
// must be "a named, actionable surface — not a generic error", each keeping
// the user's work and each offering a way to continue. Ten bespoke divs is
// exactly the class G-22 forbids for markup and G-27 forbids for error copy.
//
// ── The two things it guarantees ──────────────────────────────────────
// 1. **A name.** `title` is required. A notice a person cannot name ("what
//    went wrong?" → "an error") is the surface this primitive exists to
//    stop shipping.
// 2. **It is announced.** Failure copy that renders silently mid-flow is
//    invisible to a screen-reader user who has just pressed a button and is
//    still focused on it. `danger`/`warning` mount as `role="alert"`,
//    `info`/`success` as `role="status"` — announced on insertion, which is
//    precisely when these render.
//
// `actions` is a slot rather than a fixed label/onClick pair (the shape
// EmptyState took) because these states routinely offer TWO genuinely
// different continuations — "try again" and "carry on without the AI" —
// and one of them is often a link, not a button.
//
// ── Colour ────────────────────────────────────────────────────────────
// Semantic tokens only (`src/styles/tokens.css`), and the tone drives the
// BORDER, the faint surface tint and the (decorative, `aria-hidden`) icon —
// never the body copy. Text stays `--foreground` / `--muted-foreground`,
// the two roles `scripts/contrast-check.mjs` actually verifies at AA in
// both themes. That is deliberate: `--warning` is a 96%-saturation amber
// that does NOT reach 4.5:1 against `--background` in light mode, so a
// primitive that rendered warning copy in it would ship an unreadable
// sentence the token check does not cover. Tinted text is the Badge's job
// (11px uppercase chrome, not prose).

export type CalloutTone = 'info' | 'warning' | 'danger' | 'success';

const TONE_VAR: Record<CalloutTone, string> = {
  info: '--primary',
  warning: '--warning',
  danger: '--destructive',
  success: '--success',
};

const TONE_ICON: Record<CalloutTone, typeof InformationCircleIcon> = {
  info: InformationCircleIcon,
  warning: ExclamationTriangleIcon,
  danger: XCircleIcon,
  success: CheckCircleIcon,
};

export interface CalloutProps {
  /** Drives the border/tint/icon only — never the text colour (see above). */
  tone?: CalloutTone;
  /** The NAME of the state. Required: an unnamed notice is a generic error. */
  title: string;
  /** The explanation — normally the server's own sentence (G-27). */
  children?: ReactNode;
  /**
   * The way(s) forward. Compose DS `Button`s here. A failure Callout with no
   * action is an apology, which is the thing P04 §7 ¶4 rules out.
   */
  actions?: ReactNode;
  /** Semantic rank for `title`. Defaults to `3` (`<h3>`), as EmptyState does. */
  headingLevel?: 2 | 3 | 4 | 5 | 6;
  className?: string;
  'data-testid'?: string;
}

export function Callout({
  tone = 'info',
  title,
  children,
  actions,
  headingLevel = 3,
  className = '',
  'data-testid': testId,
}: CalloutProps): JSX.Element {
  const Heading = `h${headingLevel}` as 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
  const Icon = TONE_ICON[tone];
  const v = TONE_VAR[tone];

  return (
    <section
      // Announced on insertion — which is when a failure Callout appears.
      // `alert` is assertive (the user just pressed something and it did not
      // work); `status` is polite (progress, warnings about what is ahead).
      role={tone === 'danger' || tone === 'warning' ? 'alert' : 'status'}
      data-tone={tone}
      data-testid={testId}
      className={`flex gap-3 rounded-xl border p-3 ${className}`}
      style={{
        borderColor: `hsl(var(${v}) / 0.35)`,
        backgroundColor: `hsl(var(${v}) / 0.08)`,
      }}
    >
      {/* Decorative: the tone is already carried by the title's words, so
          this is aria-hidden and exempt from the 3:1 non-text contrast rule
          that would otherwise rule out the amber. */}
      <Icon className="mt-0.5 h-5 w-5 shrink-0" style={{ color: `hsl(var(${v}))` }} aria-hidden="true" />
      <div className="min-w-0 flex-1 space-y-2">
        <Heading className="text-sm font-semibold text-foreground">{title}</Heading>
        {children && <div className="text-sm text-muted-foreground">{children}</div>}
        {actions && <div className="flex flex-wrap items-center gap-2 pt-0.5">{actions}</div>}
      </div>
    </section>
  );
}
